import { ReportMetadata, ServiceItem, UserProfile } from '../types';
import { loadProjectById, saveProjectToCloud } from './cloudService';
import { getServiceLog, mirrorEntriesToJournal } from './serviceJournal';
import { altCoveredQuantities } from './externalAdvances';
import { FlatSwitchDraft, planFlatSwitch } from './flatRateSwitchLogic';

/**
 * Schreibpfad der UMSTELLUNG auf die Monatspauschale — die einzige Stelle,
 * die sie persistiert.
 *
 * Die REGELN liegen daneben in `flatRateSwitchLogic.ts` und sind ohne
 * Supabase pruefbar; hier steht nur, was ohne Datenbank nicht zu haben ist.
 * Dasselbe Muster wie serviceWaiverLogic/serviceWaiverService.
 *
 * Frisch geladen wird IMMER, und der harte Riegel laeuft ERNEUT gegen den
 * frischen Stand (planFlatSwitch prueft selbst): der Dialog rechnet auf einem
 * Abzug vom letzten Refresh, und zwischen Anzeigen und Klick kann eine
 * Buchung dazugekommen sein — genau die Menge, die die Deckung sonst lautlos
 * verschlucken wuerde.
 *
 * Geschrieben wird ueber `saveProjectToCloud`, nicht per direktem INSERT auf
 * `project_services`: die Position lebt in ZWEI Quellen (relational und die
 * JSON-Spiegelung), und nur dieser Pfad schreibt beide — einschliesslich
 * `flat_rate` (Spaltenliste seit SQL Nr. 53). Das abgeleitete `flatCoveredBy`
 * entsteht erst beim Laden (stampFlatCoverage) und wird hier nie geschrieben.
 */

const actorNameOf = (user: UserProfile | null | undefined): string =>
    user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Buchhaltung' : 'Buchhaltung';

export interface FlatSwitchCommand {
    projectId: string;
    draft: FlatSwitchDraft;
    user: UserProfile | null;
}

export interface FlatSwitchOutcome {
    ok: boolean;
    /** Fuer die Oberflaeche: was schiefging bzw. was geschah (inkl. offener Monate). */
    message: string;
}

/** Ein laufendes Projekt auf die Monatspauschale umstellen. */
export const switchProjectToFlatRate = async (
    command: FlatSwitchCommand,
): Promise<FlatSwitchOutcome> => {
    let fullProject: any;
    try {
        fullProject = await loadProjectById(command.projectId);
    } catch (e: any) {
        return { ok: false, message: `Projekt konnte nicht geladen werden: ${e?.message || e}` };
    }
    if (!fullProject) return { ok: false, message: 'Projekt nicht gefunden.' };

    const meta = fullProject.data?.metadata as ReportMetadata | undefined;
    const globalData = meta?.globalData;
    if (!meta || !globalData) {
        return { ok: false, message: 'Das Projekt hat keine lesbaren Stammdaten.' };
    }
    const services: ServiceItem[] = globalData.services || [];
    const serviceLog = getServiceLog({ metadata: meta });

    const now = new Date().toISOString();
    const result = planFlatSwitch(
        {
            services,
            altCovered: altCoveredQuantities(serviceLog),
            startDate: globalData.startDate,
            endDate: globalData.endDate,
            today: now.slice(0, 10),
            // Der Riegel zaehlt nur, was vor dem Stichtagsmonat erbracht ist.
            serviceLog,
        },
        command.draft,
        {
            flatServiceId: crypto.randomUUID(),
            now,
            actorName: actorNameOf(command.user),
            actorId: command.user?.id,
            makeEntryId: () => crypto.randomUUID(),
        },
    );
    // Laut und ohne Schreibvorgang scheitern (dieselbe Begruendung wie beim
    // Verzicht): wer glaubt, umgestellt zu haben, rechnet die naechsten
    // Monate falsch ab.
    if (!result.ok || !result.plan) {
        return { ok: false, message: result.error || 'Die Umstellung konnte nicht geplant werden.' };
    }
    const plan = result.plan;

    const note = {
        id: crypto.randomUUID(),
        text: plan.noteText,
        date: now,
        author: actorNameOf(command.user),
    };

    try {
        await saveProjectToCloud(
            {
                ...fullProject.data,
                metadata: {
                    ...meta,
                    globalData: {
                        ...globalData,
                        services: [...services, plan.service],
                        serviceLog: [...plan.lockEntries, ...serviceLog],
                        notes: [note, ...(globalData.notes || [])],
                    },
                },
            },
            fullProject.id,
        );
    } catch (e: any) {
        return { ok: false, message: `Speichern fehlgeschlagen: ${e?.message || e}` };
    }

    // Zweitschrift ins Journal — erst nach dem BESTAETIGTEN Speichern und
    // ausserhalb des try darueber: das Anhaengen darf den Ausgang dieses
    // Vorgangs nicht mehr aendern. Angehaengt werden genau die Sperr-Eintraege
    // der Umstellung, nichts aus dem Altbestand.
    await mirrorEntriesToJournal({
        projectId: fullProject.id,
        entries: plan.lockEntries,
        previousLog: serviceLog,
        source: 'flat_rate_switch',
        linkedProjectConfig: globalData.linkedProjectConfig ?? null,
    });

    return { ok: true, message: plan.noteText };
};
